import DOMPurify from 'dompurify';
import katex from 'katex';

import { MATH_FIELD } from './constants';

/**
 * Render KaTeX + markdown-like formatting.
 * Math expressions are displayed on new lines.
 */
export const renderMarkupWithMath = ({ text }) => {
  if (!text) return '';

  let html = text;

  // Convert double escaped backslashes from BE ("\\sqrt") → "\sqrt"
  html = html.replace(/\\\\/g, '\\');

  // --- MATH FIRST ---

  // Block math: $$...$$ → full-width centered block
  html = html.replace(
    /\$\$([\s\S]+?)\$\$/g,
    (_, tex) =>
      `<div class="math-block">${katex.renderToString(tex.trim(), {
        throwOnError: false,
        displayMode: true
      })}</div>`
  );

  // Inline math: $...$ → render and add line breaks around
  html = html.replace(
    /\$([^$]+)\$/g,
    (_, tex) =>
      `<div class="math-inline">${katex.renderToString(tex.trim(), {
        throwOnError: false,
        displayMode: false
      })}</div>`
  );

  html = DOMPurify.sanitize(html);

  // --- MARKDOWN HEADERS ---
  html = html
    .replace(/^### (.*)$/gim, '<h3>$1</h3>')
    .replace(/^## (.*)$/gim, '<h2>$1</h2>')
    .replace(/^# (.*)$/gim, '<h1>$1</h1>');

  // --- LISTS ---
  html = html
    .replace(/^[*-]\s+(.*)$/gim, '<ul><li>$1</li></ul>')
    .replace(/^\d+\.\s+(.*)$/gim, '<ol><li>$1</li></ol>')
    .replace(/<\/ul>\s*<ul>/g, '')
    .replace(/<\/ol>\s*<ol>/g, '');

  // --- INLINE STYLES ---
  html = html
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<u>$1</u>')
    .replace(/__(.*?)__/g, '<u>$1</u>')
    .replace(/(?<!\S)\*(?!\s)(.+?)(?<!\s)\*(?!\S)/g, '<em>$1</em>');

  // --- REMOVE DOTS DIRECTLY AFTER MATH EXPRESSIONS (+ trim nearby spaces) ---
  html = html.replace(/(<\/span>|<\/div>)\s*\.\s*/g, '$1 ');

  // Remove commas directly after math expressions (+ trim nearby spaces)
  html = html.replace(/(<\/span>|<\/div>)\s*,\s*/g, '$1 ');

  // --- NEWLINES ---
  html = html.replace(/\\n/g, '<br>'); // handle "\n" literals

  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  return html.trim();
};

// Helper function for EditorField
export const traverseNodes = ({ node, parentTag = '' }) => {
  if (!node) return '';

  // Handle text nodes
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }

  // Handle <br> explicitly (important!)
  if (
    node.nodeType === Node.ELEMENT_NODE &&
    node.tagName.toLowerCase() === 'br'
  ) {
    return '\\n'; // store as escaped newline
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const tag = node.tagName.toLowerCase();

    // Handle math-field explicitly
    if (tag === MATH_FIELD) {
      return `$${node.value}$`;
    }

    // Recursively serialize children
    const inner = Array.from(node.childNodes)
      .map((child) => traverseNodes({ node: child, parentTag: tag }))
      .join('');

    // Inline styles
    switch (tag) {
      case 'strong':
      case 'b':
        return `**${inner}**`;
      case 'em':
      case 'i':
        return `*${inner}*`;
      case 'u':
        return `_${inner}_`;
      case 's':
      case 'del':
        return `~~${inner}~~`;
      default:
        // For tags that aren't handled, just return the inner content
        return inner;
    }
  }

  return '';
};
